/**
 * @file token.interceptor.ts
 */
import { Inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConstants } from '@root/app/app.constants';
import { environment } from '@root/environments/environment';
import { VersionOverrideType } from '@root/api-versions/api-version-config';
import { AppInsightsService, UserService } from '@shared/services';
import { BrandConfig } from '@models/interfaces';
import { BRAND_CONFIG } from '@core/injection-tokens';
import { TokenService } from '@core/services';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly userService: UserService,
    private readonly tokenService: TokenService,
    private readonly insightsService: AppInsightsService,
    @Inject(BRAND_CONFIG) private readonly brandConfig: BrandConfig ) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const providerType = this.userService.getProviderType();

    // get API version to pass in header
    const version = this.getAPIVersion(request.url, request.method);

    request = request.clone({
      setHeaders: {
        Authorization: this.tokenService.azureIdToken || '',
        'Authorization-SSO': this.tokenService.ssoToken || '',
        'Ocp-Apim-Subscription-Key': environment.ocpApimSubscriptionKey,
        'ProviderType': providerType ? providerType : AppConstants.GUEST_PROVIDER_TYPE,
        'x-api-version': version,
        'Brand-Name': this.brandConfig.key
      }
    });
    return next.handle(request);
  }

  private getAPIVersion(url: string, method: string): string {
    // set API version to default version
    let version = environment.apiVersionConfig.defaultVersion;

    try {
      let baseUrl = url.split('?')[0];
      baseUrl = baseUrl.replace(environment.hostURL, "");

      const apiOverrides: VersionOverrideType = environment.apiVersionConfig.versionOverrides;

      if (apiOverrides.hasOwnProperty(baseUrl)) {
        const apiDetails = apiOverrides[baseUrl];
        if (apiDetails.methods && apiDetails.methods.length > 0) {
          if (apiDetails.methods.includes(method)) {
            version = apiDetails.apiVersion;
          }
        } else {
          // if no method is specified, then set the overridden API version for all the APIs with baseURL
          version = apiDetails.apiVersion;
        }
      }
      return version;
    } catch (error) {
      const errorObj: Error = {
        name: "ADD API VERSION",
        message: `Exception while calculation API version`,
        stack: JSON.stringify(error)
      }
      this.insightsService.trackException(errorObj, 1);
      return version; // return default version
    }
  }
}
